import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ClipboardCheck, FileText, Users, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHero, Reveal, Eyebrow, SectionHeading } from "../components/Shared";
import { IMAGES, AUDIENCES } from "../mock";
import { createReviewRequest, errorMessage } from "../lib/api";

const ORG_SIZES = ["Under $500K annual budget", "$500K - $2M", "$2M - $10M", "Over $10M"];
const initial = { organization: "", contactName: "", title: "", email: "", phone: "", isMember: "yes", locations: "", orgSize: "", preferredTiming: "", notes: "" };
const offers = AUDIENCES[0].offers;

const steps = [
  { icon: ClipboardCheck, title: "Two-hour on-site review", text: offers[0].text },
  { icon: FileText, title: "Written recommendations report", text: offers[1].text },
  { icon: Users, title: "Leadership meeting", text: offers[2].text },
];

const ReviewRequest = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.organization.trim()) e.organization = "Please enter your organization.";
    if (!form.contactName.trim()) e.contactName = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      const rec = await createReviewRequest({ ...form, isMember: form.isMember === "yes" });
      setDone(rec);
      toast.success("Review request received.", { description: "We'll reach out to schedule your two-hour review." });
    } catch (err) {
      toast.error("We couldn't send your request.", { description: errorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="NH Center for Nonprofits members"
        title="Request your free Operational & Property Review."
        intro="A two-hour review by Great North Advantage management that identifies cost-saving opportunities and areas of operational and financial risk, followed by a written recommendations report."
        image={IMAGES.volunteers}
      />
      <section className="section" data-testid="review-request-content">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>What members receive</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl leading-tight text-ink-900">Three steps. No cost. No obligation.</h2>
              <ol className="mt-8 space-y-6">
                {steps.map((s, i) => (
                  <li key={s.title} className="flex gap-5">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-700"><s.icon size={22} /></span>
                    <div>
                      <p className="font-mono text-[11px] tracking-widest text-brand-600">STEP {i + 1}</p>
                      <h3 className="mt-1 font-serif text-xl text-ink-900">{s.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{s.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-10 rounded-2xl border border-brand-100 bg-white p-6">
                <p className="flex items-center gap-2 text-sm font-semibold text-ink-900"><Clock size={16} className="text-brand-600" /> Typical turnaround</p>
                <p className="mt-2 text-sm leading-relaxed text-ink-600">We confirm your request within two business days and schedule the review at a time that works for your staff and board.</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={100}>
              {done ? (
                <div className="rounded-3xl border border-brand-100 bg-white p-8 lg:p-10" data-testid="review-success">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-700"><CheckCircle2 size={28} /></span>
                  <h3 className="mt-6 font-serif text-3xl text-ink-900">Request received.</h3>
                  <p className="mt-3 text-ink-600">Thank you, {done.contactName.split(" ")[0]}. We'll contact you at <span className="font-medium text-ink-900">{done.email}</span> to schedule the Operational &amp; Property Review for <span className="font-medium text-ink-900">{done.organization}</span>.</p>
                  <p className="mt-2 text-xs text-ink-500">Reference: {done.id}</p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Button asChild className="btn-pill bg-brand-600 text-white hover:bg-brand-700"><Link to="/what-we-do">Explore our services <ArrowRight size={15} className="ml-1.5" /></Link></Button>
                    <Button variant="outline" onClick={() => { setForm(initial); setDone(null); }} className="btn-pill border-brand-200 text-brand-700 hover:bg-brand-50" data-testid="review-reset">Submit another request</Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} noValidate className="rounded-3xl border border-brand-100 bg-white p-6 lg:p-10" data-testid="review-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="organization">Organization *</Label>
                      <Input id="organization" value={form.organization} onChange={(e) => set("organization", e.target.value)} placeholder="Seacoast Early Learning Alliance" className="h-11 rounded-xl" data-testid="review-organization" />
                      {errors.organization && <p className="text-xs text-red-600">{errors.organization}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contactName">Your name *</Label>
                      <Input id="contactName" value={form.contactName} onChange={(e) => set("contactName", e.target.value)} placeholder="Jane Smith" className="h-11 rounded-xl" data-testid="review-contact-name" />
                      {errors.contactName && <p className="text-xs text-red-600">{errors.contactName}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="title">Title / role</Label>
                      <Input id="title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Executive Director" className="h-11 rounded-xl" data-testid="review-title" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@organization.org" className="h-11 rounded-xl" data-testid="review-email" />
                      {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(603) 555-0100" className="h-11 rounded-xl" data-testid="review-phone" />
                    </div>
                    <div className="space-y-3 sm:col-span-2">
                      <Label>Are you a NH Center for Nonprofits member?</Label>
                      <RadioGroup value={form.isMember} onValueChange={(v) => set("isMember", v)} className="flex gap-3">
                        {[["yes", "Yes, we're a member"], ["no", "Not yet"]].map(([v, l]) => (
                          <label key={v} className="flex flex-1 cursor-pointer items-center gap-3 rounded-xl border border-brand-100 px-4 py-3 text-sm text-ink-700 transition-colors hover:bg-brand-50 has-[[data-state=checked]]:border-brand-500 has-[[data-state=checked]]:bg-brand-50">
                            <RadioGroupItem value={v} data-testid={`review-member-${v}`} /> {l}
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="locations">Number of locations / properties</Label>
                      <Input id="locations" value={form.locations} onChange={(e) => set("locations", e.target.value)} placeholder="e.g. 2" className="h-11 rounded-xl" data-testid="review-locations" />
                    </div>
                    <div className="space-y-2">
                      <Label>Organization size</Label>
                      <Select value={form.orgSize} onValueChange={(v) => set("orgSize", v)}>
                        <SelectTrigger className="h-11 rounded-xl" data-testid="review-org-size"><SelectValue placeholder="Select one" /></SelectTrigger>
                        <SelectContent>{ORG_SIZES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="preferredTiming">Preferred timing</Label>
                      <Input id="preferredTiming" value={form.preferredTiming} onChange={(e) => set("preferredTiming", e.target.value)} placeholder="e.g. Weekday mornings in the next month" className="h-11 rounded-xl" data-testid="review-timing" />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="notes">Anything we should know?</Label>
                      <Textarea id="notes" rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Current challenges, upcoming projects, areas you'd like us to focus on." className="rounded-xl" data-testid="review-notes" />
                    </div>
                  </div>
                  <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-ink-500">Free for NH Center for Nonprofits members. No obligation.</p>
                    <Button type="submit" disabled={submitting} className="btn-pill h-12 bg-brand-600 px-8 text-white hover:bg-brand-700" data-testid="review-submit">
                      {submitting ? "Sending..." : "Request my review"} {!submitting && <ArrowRight size={16} className="ml-2" />}
                    </Button>
                  </div>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
      <section className="section bg-white">
        <div className="container-x">
          <SectionHeading align="center" eyebrow="Why it matters" title="Redirect administrative dollars back into your mission." intro="Members of the Seacoast Early Learning Alliance used shared services to move resources historically allocated to administration back into the classroom. The review is where that conversation starts." />
        </div>
      </section>
    </>
  );
};

export default ReviewRequest;
